import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    role_choice: "member",
    referral_code: searchParams.get("ref") || "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referral_code: ref }));
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register({
        ...form,
        referral_code: form.referral_code?.trim() || undefined,
      });
      toast.success(t("auth.welcome"));
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7] px-6 py-16 relative overflow-hidden">
      <div className="absolute top-6 left-6"><Logo size="md" /></div>
      <div className="absolute top-6 right-6"><LanguageToggle /></div>

      <div className="w-full max-w-lg" data-testid="register-page">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
            sign up
          </div>
          <h1 className="mt-3 font-serif-cn text-3xl md:text-4xl text-[#003645] font-semibold">
            {t("auth.createAccount")}
          </h1>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-4 bg-white border border-[#e5ebeb] rounded-lg p-8">
          <GoogleAuthButton referralCode={form.referral_code} dataTestId="register-google-btn" labelKey="auth.googleSignUp" />
          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-[#e5ebeb]" />
            <span className="mx-3 text-[11px] text-[#4a6e77] uppercase tracking-widest">{t("auth.or")}</span>
            <div className="flex-grow border-t border-[#e5ebeb]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.name")}</label>
              <input
                required
                className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none transition-colors duration-200"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.phone")}</label>
              <input
                className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none transition-colors duration-200"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="register-phone-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.email")}</label>
            <input
              type="email"
              required
              className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none transition-colors duration-200"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.password")}</label>
            <input
              type="password"
              required
              minLength={6}
              className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none transition-colors duration-200"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="register-password-input"
            />
          </div>

          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.identity")}</label>
            <div className="grid grid-cols-2 gap-2" data-testid="register-role-toggle">
              {["member", "agent"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setForm({ ...form, role_choice: r })}
                  className={`px-4 py-2.5 rounded-md text-sm font-medium border transition-colors duration-200 ${
                    form.role_choice === r
                      ? "bg-[#005870] text-white border-[#005870]"
                      : "bg-white text-[#003645] border-[#e5ebeb] hover:border-[#00868e]"
                  }`}
                  data-testid={`role-${r}-btn`}
                >
                  {t(`auth.${r}`)}
                </button>
              ))}
            </div>
            {form.role_choice === "agent" && (
              <div className="mt-2 text-xs text-[#4a6e77] leading-relaxed" data-testid="agent-note">
                {t("auth.agentNote")}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.referralCode")}</label>
            <div className="relative">
              <input
                className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none transition-colors duration-200 uppercase tracking-wider"
                value={form.referral_code}
                onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                data-testid="register-referral-input"
                placeholder="AIKO2026"
              />
              {form.referral_code && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#00868e]">
                  <CheckCircle2 size={16} />
                </div>
              )}
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-600" data-testid="register-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
            data-testid="register-submit-btn"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t("auth.register")}
          </button>
          <div className="text-center text-sm text-[#4a6e77]">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" className="text-[#005870] hover:text-[#00868e] font-medium">
              {t("auth.loginHere")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
